import linkifyElement from "linkifyjs/element";
import isEqual from "lodash.isequal";

// Rich text related types, copied from "version_1.ts"
export type RichText = RichNode[] | string;
export type RichNode = RichTag | string;
export type RichAttributes = {
  [key in "href" | "target" | "style" | "align" | "data-id" | "title" | "rel" | "target" | "o-href" | "track-href" | "class"]?:
    | string
    | null;
};
export type RichTag = {
  // Attributes
  a?: RichAttributes;
  // Children can be a tag or string
  c?: RichNode[];
  // tag name
  t: "p" | "strong" | "span" | "em" | "u" | "a" | "br" | "ul" | "ol" | "li" | "blockquote";
};

type AllowedTags = "p" | "strong" | "span" | "em" | "u" | "a" | "br" | "ul" | "ol" | "li" | "blockquote";

const NOT_ALLOWED_RECURSIVE_TAGS: AllowedTags[] = ["span"];

const ALLOWED_TAGS: Record<AllowedTags, true> = {
  a: true,
  strong: true,
  em: true,
  br: true,
  p: true,
  u: true,
  ul: true,
  li: true,
  ol: true,
  span: true,
  blockquote: true
};

export const URL_REGEXP: RegExp = /^(http|https:\/\/)?(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?$/i;
export const EMAIL_URL_REGEXP: RegExp = /^(mailto:)?([A-Z0-9._%+-]+@(?:[A-Z0-9-]+\.)+[A-Z]{2,6})$/i;
export const TEL_URL_REGEXP: RegExp = /^(tel:)?(\+[0-9]{1,3})?([-])?([0-9\-]{7,})$/i;
const TEXT_ALIGN_REGEX = /^text-align:\s?(left|right|center|justify);?$/i;
const TEXT_ALIGN_ATTR_REGEX = /^(left|right|center|justify)$/i;

const DEFAULT_TRANSFORM_MAP: Record<string, AllowedTags> = {
  p: "p",
  div: "p",
  b: "strong",
  strong: "strong",
  span: "span",
  em: "em",
  i: "em",
  u: "u",
  a: "a",
  br: "br",
  h1: "p",
  h2: "p",
  h3: "p",
  h4: "p",
  h5: "p",
  h6: "p",
  tr: "p",
  td: "span",
  ul: "ul",
  ol: "ol",
  li: "li",
  blockquote: "blockquote"
};

/***
 * Used by the rich text editor
 *
 */
export function domToRichText(el?: HTMLElement): RichNode[] {
  if (!el || !el.childNodes.length) {
    return [];
  }

  const domNodes = Array.from(el.childNodes);

  const result: RichNode[] = [];

  domNodes.forEach((n) => {
    switch (n.nodeType) {
      // we only deal with text node and element node

      case Node.TEXT_NODE:
        // clean it up
        const t = (n as Text).data.replace(/\s+/g, " ");
        // push it to our result if we have any text
        t && result.push(t);
        break;

      case Node.ELEMENT_NODE:
        const el: RichTag | undefined = sanitizeDomElement(n as HTMLElement);

        // if we didn't get anything, return
        if (!el) {
          return;
        }

        // add to our output
        result.push(el);
        break;
    }
  });

  return result;
}

// turn text nodes with "http://wwww.smore.com" to links <a href="http://www.smore.com">http://www.smore.com</a>
// Using the linkify lib (https://soapbox.github.io/linkifyjs)
export function autoLinkify(el: HTMLElement) {
  linkifyElement(el);
}

// Trim empty paragraphs from the start / end of the element
export function trimHtml(el: Element) {
  // first -> last
  let child: ChildNode | null;
  while ((child = el.firstChild)) {
    const t = child.textContent?.trim() || "";

    if (t) {
      break;
    }

    el.removeChild(child);
  }

  // last -> first
  while ((child = el.lastChild)) {
    const t = child.textContent?.trim() || "";

    if (t) {
      break;
    }
    el.removeChild(child);
  }
}

function sanitizeDomElement(el: HTMLElement): RichTag | undefined {
  // if we don't have a simple tag name, skip it
  if (!el.localName) {
    return;
  }

  // get our tag name
  const ogTag = el.localName.toLowerCase();
  if (ogTag === "style") {
    // We skip random style tags
    return;
  }
  const t = DEFAULT_TRANSFORM_MAP[ogTag] || "span";

  switch (t) {
    case "blockquote":
      return sanitizeDomBlockquote(el as HTMLElement);
    case "span":
      return sanitizeDomSpan(el as HTMLSpanElement);
    case "p":
    case "li":
      return sanitizeDomPLike(t, el as HTMLParagraphElement, t == "p");
    case "a":
      return sanitizeDomA(el as HTMLAnchorElement);
    default:
      // empty node
      return elToRichTag(t, el);
  }
}

const SPAN_STYLE_TRANSFORM: Array<{ test: RegExp; tag: AllowedTags }> = [
  { test: /font-weight: (bold|700)/i, tag: "strong" },
  { test: /(text-decoration: underline|text-decoration-line: underline)/i, tag: "u" },
  { test: /(font-style: italic)/i, tag: "em" }
];

// Chrome browser uses `blockquote` for indenting text
function sanitizeDomBlockquote(el: HTMLElement): RichTag {
  const style = el.getAttribute("style") || "";
  const attrs: RichAttributes = {};

  attrs.style = style;

  return elToRichTag("blockquote", el, attrs);
}
// Some browsers use span tags instead of HTML bold and strong etc.
// we process them
function sanitizeDomSpan(el: HTMLSpanElement): RichTag {
  const style = el.getAttribute("style") || "";
  const alignAttr = el.getAttribute("align");
  const transform = SPAN_STYLE_TRANSFORM.find((t) => t.test.test(style));
  const attrs: RichAttributes = {};

  if (style && TEXT_ALIGN_REGEX.test(style)) {
    attrs.style = style;
  }

  // TODO: maybe add text-align test?
  // convert align=right|left to style attribute
  if (alignAttr && TEXT_ALIGN_REGEX.test(alignAttr)) {
    attrs.style = `text-align: ${alignAttr};`;
  }

  return elToRichTag(transform ? transform.tag : "span", el, attrs);
}

function sanitizeDomPLike(tag: AllowedTags, el: HTMLParagraphElement, addBr: boolean = false) {
  const styleAttr = el.getAttribute("style");
  const alignAttr = el.getAttribute("align");

  const attrs: RichAttributes = {};

  // Allow style: text-align:left/right

  if (styleAttr && TEXT_ALIGN_REGEX.test(styleAttr)) {
    attrs.style = styleAttr;
  }

  // convert align=right|left to style attribute
  if (alignAttr && TEXT_ALIGN_ATTR_REGEX.test(alignAttr)) {
    attrs.style = `text-align: ${alignAttr};`;
  }

  const res = elToRichTag(tag, el, attrs);

  // add an empty br inside a p, if we don't have any children
  if ((!res.c || !res.c.length) && addBr) {
    res.c = [elToRichTag("br")];
  }

  return res;
}

function sanitizeDomA(el: HTMLAnchorElement): RichTag {
  return elToRichTag("a", el, {
    "href": el.getAttribute("href"),
    "data-id": el.getAttribute("data-id") || linkId(),
    "target": "_blank"
  });
}

function linkId() {
  var s = "l-";

  for (var i = 0; i < 10; i++) {
    s += Math.floor(Math.random() * 10);
  }

  return s;
}

function isRichTag(input: RichTag | string): input is RichTag {
  return !!input && typeof input === "object" && input.t in ALLOWED_TAGS;
}

function sameAttrs(a?: RichAttributes, b?: RichAttributes) {
  return isEqual(a, b);
}

export function coerceUrl(url: string) {
  let rawUrl = url.trim();
  if (EMAIL_URL_REGEXP.test(rawUrl)) {
    if (!rawUrl.startsWith("mailto:")) {
      rawUrl = "mailto:" + url;
    }
  } else if (TEL_URL_REGEXP.test(rawUrl)) {
    if (!rawUrl.startsWith("tel:")) {
      rawUrl = "tel:" + url;
    }
  } else if (URL_REGEXP.test(rawUrl) && !rawUrl.startsWith("http://") && !rawUrl.startsWith("https://")) {
    rawUrl = "https://" + url;
  }
  return rawUrl;
}

function elToRichTag(t: AllowedTags, el?: HTMLElement, a: RichAttributes = {}): RichTag {
  let c = el ? domToRichText(el) : [];

  // Fix an issue where we have "span" inside a span for no reason
  if (c.length === 1) {
    const child = c[0];
    if (isRichTag(child) && child.t === t && NOT_ALLOWED_RECURSIVE_TAGS.includes(t) && sameAttrs(a, child.a)) {
      c = child.c || [];
    }
  }
  return { t, c, a };
}

function sanitizeNodes(nodes?: RichNode[], transformMap: Record<string, AllowedTags> = DEFAULT_TRANSFORM_MAP): RichNode[] {
  let result: RichNode[] = [];
  if (!nodes) return result;

  nodes.forEach((n) => {
    // handle text node
    if (typeof n === "string") {
      result.push(n);
      return;
    }

    // change the tag using our transform map
    n.t = transformMap[n.t];

    // sanitize children
    const children = sanitizeNodes(n.c);
    const attrs = sanitizeAttrs(n.t, n.a);

    // we don't allow links with no href
    if (n.t in ALLOWED_TAGS && !(n.t === "a" && !attrs.href)) {
      result.push({
        t: n.t,
        c: children,
        a: attrs
      });
    } else {
      // not allowed, we merge the children to the result
      result = result.concat(children);
    }
  });

  return result;
}

function sanitizeAttrs(tag: AllowedTags, attrs?: RichAttributes): RichAttributes {
  const result: RichAttributes = {};
  if (attrs) {
    Object.entries(attrs).forEach(([name, value]) => {
      switch (name) {
        case "data-id":
        case "title":
          result[name] = value;
          break;
        case "href":
          if (value && (URL_REGEXP.test(value) || EMAIL_URL_REGEXP.test(value) || TEL_URL_REGEXP.test(value))) {
            result[name] = value;
          }
          break;
        case "style":
          if (value && tag in { p: 1, li: 1 } && TEXT_ALIGN_REGEX.test(value)) {
            result["style"] = value;
          }
      }
    });

    if (tag === "blockquote") {
      result["style"] = "margin: 0 0 0 40px; border: none; padding: 0px;";
    }

    if (tag === "a") {
      result["rel"] = "nofollow";
      result["target"] = "_blank";
    }
  }

  return result;
}

/***
 * Convert a string to nodes
 * @param {String} str
 * @return {Array} nodes
 */
function coerceNodes(input: RichText): RichNode[] {
  if (typeof input === "string") {
    return [{ t: "p", c: [input || { t: "br" }] }];
  }
  return input;
}

/****
 * Renders AND SANITIZE the given nodes array
 *
 * @param {Array|String} nodes
 * @returns {string}
 */
export function renderHTML(nodes: RichText, transform?: CustomNodeTransformer) {
  return renderToString(sanitizeNodes(coerceNodes(nodes)), transform);
}

/***
 * Convert nodes to text
 * @param node
 * @returns {*}
 */
function nodeToText(node: RichNode): string {
  if (typeof node == "string") {
    return node;
  }

  let r = node.c ? node.c.map(nodeToText).join("") : "";

  if (node.t === "p" || node.t === "br" || node.t === "li" || node.t === "blockquote") {
    r += "\n";
  }

  return r;
}

/***
 *
 * @param {Array|String} nodes
 * @returns {string}
 */
export function toText(nodes: RichText | undefined) {
  if (!nodes) {
    return "";
  }
  return coerceNodes(nodes).map(nodeToText).join("");
}

/**************************** Rendering ************************************/

/****
 * Generates string representation of the given nodes
 *
 * @param {Array} nodes
 * @returns {string}
 */
function renderToString(nodes?: RichNode[], transform?: CustomNodeTransformer) {
  return nodes ? nodes.map((n) => nodeToString(n, transform)).join("") : "";
}

export type CustomNodeTransformer = (node: RichTag) => RichTag;

/****
 *
 * @param {Node} node
 */
function nodeToString(node: RichNode, transform?: CustomNodeTransformer): string {
  if (typeof node === "string") {
    if (isNonLegalChars(node)) {
      return "";
    }

    return t(node);
  }

  if (node.t) {
    if (isNonLegalChars(node.t)) {
      return "";
    }

    // transform node
    node = transform ? transform(node) : node;

    // handle br quickly
    if (node.t === "br") {
      return "<br/>";
    }

    // escape the tag
    const tag = t(node.t);

    // render attributes
    const attrs = Object.entries(node.a || {})
      .map(([name, value]) => {
        if (isNonLegalChars(name) || !value) {
          return "";
        }

        return ` ${t(name)}="${t(value)}"`;
      })
      .join("");

    return `<${tag}${attrs}>${renderToString(node.c, transform)}</${tag}>`;
  }

  // not a dom instance
  throw "NodeNotSupported";
}

/***
 * Escape a given string as text node
 * @param {String} str
 * @returns {String}
 *
 * "Stolen" from the xmlbuilder lib
 * http://github.com/oozcitak/xmlbuilder-js/
 */
function t(str: string) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/'/g, "&apos;").replace(/"/g, "&quot;");
}

/****
 * Checks the string for illegal chars
 * @param {string} str
 * @returns {boolean}
 *
 * "Stolen" from the xmlbuilder lib
 * http://github.com/oozcitak/xmlbuilder-js/
 */
function isNonLegalChars(str: string) {
  const isEmoji = !!str.match(EMOJI_RE);
  // Some unicode characters are 32-bit vs older 16bit ones (e.g. music symbols). This means they consist of 5-char-hex
  // sequences (e.g. 0x1D100) and not the regular 4-char-hex. Those can also be represented as two sequential
  // 4-char-hex representation (e.g. 0x1D100 == 0xD834 and 0xDD00).
  // Therefore, for now, we removed "\uD800-\uDFFF" from the regex so we can support the aforementioned unicode chars.
  //
  // See http://www.russellcottrell.com/greek/utilities/SurrogatePairCalculator.htm for converting 5-char-hex to 4-char-hex.

  // const isIllegalChar = !!str.match(/[\u0000-\u0008\u000B-\u000C\u000E-\u001F\uD800-\uDFFF\uFFFE-\uFFFF]/);
  const isIllegalChar = !!str.match(/[\u0000-\u0008\u000B-\u000C\u000E-\u001F\uFFFE-\uFFFF]/);
  return isIllegalChar && !isEmoji;
}

export function attrPatch(n: RichTag, attr: keyof RichAttributes, patch: (v: string | undefined | null) => string) {
  if (!n.a) {
    n.a = {};
  }

  n.a[attr] = patch(n.a[attr]);
}

// Yay we stole this from "twemoji"
// twitter's emoji lib
const EMOJI_RE =
  /(?:\ud83d\udc68\ud83c\udffb\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc68\ud83c\udffc\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc68\ud83c\udffd\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc68\ud83c\udffe\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc68\ud83c\udfff\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffb\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffb\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffc\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffc\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffd\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffd\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffe\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffe\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udfff\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udfff\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83e\uddd1\ud83c\udffb\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83e\uddd1\ud83c[\udffc-\udfff]|\ud83e\uddd1\ud83c\udffc\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83e\uddd1\ud83c[\udffb\udffd-\udfff]|\ud83e\uddd1\ud83c\udffd\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83e\uddd1\ud83c[\udffb\udffc\udffe\udfff]|\ud83e\uddd1\ud83c\udffe\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83e\uddd1\ud83c[\udffb-\udffd\udfff]|\ud83e\uddd1\ud83c\udfff\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83e\uddd1\ud83c[\udffb-\udffe]|\ud83d\udc68\ud83c\udffb\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc68\ud83c\udffb\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffc-\udfff]|\ud83d\udc68\ud83c\udffc\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc68\ud83c\udffc\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffb\udffd-\udfff]|\ud83d\udc68\ud83c\udffd\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc68\ud83c\udffd\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffb\udffc\udffe\udfff]|\ud83d\udc68\ud83c\udffe\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc68\ud83c\udffe\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffb-\udffd\udfff]|\ud83d\udc68\ud83c\udfff\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc68\ud83c\udfff\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffb-\udffe]|\ud83d\udc69\ud83c\udffb\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffb\u200d\u2764\ufe0f\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffb\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffc-\udfff]|\ud83d\udc69\ud83c\udffb\u200d\ud83e\udd1d\u200d\ud83d\udc69\ud83c[\udffc-\udfff]|\ud83d\udc69\ud83c\udffc\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffc\u200d\u2764\ufe0f\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffc\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffb\udffd-\udfff]|\ud83d\udc69\ud83c\udffc\u200d\ud83e\udd1d\u200d\ud83d\udc69\ud83c[\udffb\udffd-\udfff]|\ud83d\udc69\ud83c\udffd\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffd\u200d\u2764\ufe0f\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffd\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffb\udffc\udffe\udfff]|\ud83d\udc69\ud83c\udffd\u200d\ud83e\udd1d\u200d\ud83d\udc69\ud83c[\udffb\udffc\udffe\udfff]|\ud83d\udc69\ud83c\udffe\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffe\u200d\u2764\ufe0f\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udffe\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffb-\udffd\udfff]|\ud83d\udc69\ud83c\udffe\u200d\ud83e\udd1d\u200d\ud83d\udc69\ud83c[\udffb-\udffd\udfff]|\ud83d\udc69\ud83c\udfff\u200d\u2764\ufe0f\u200d\ud83d\udc68\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udfff\u200d\u2764\ufe0f\u200d\ud83d\udc69\ud83c[\udffb-\udfff]|\ud83d\udc69\ud83c\udfff\u200d\ud83e\udd1d\u200d\ud83d\udc68\ud83c[\udffb-\udffe]|\ud83d\udc69\ud83c\udfff\u200d\ud83e\udd1d\u200d\ud83d\udc69\ud83c[\udffb-\udffe]|\ud83e\uddd1\ud83c\udffb\u200d\u2764\ufe0f\u200d\ud83e\uddd1\ud83c[\udffc-\udfff]|\ud83e\uddd1\ud83c\udffb\u200d\ud83e\udd1d\u200d\ud83e\uddd1\ud83c[\udffb-\udfff]|\ud83e\uddd1\ud83c\udffc\u200d\u2764\ufe0f\u200d\ud83e\uddd1\ud83c[\udffb\udffd-\udfff]|\ud83e\uddd1\ud83c\udffc\u200d\ud83e\udd1d\u200d\ud83e\uddd1\ud83c[\udffb-\udfff]|\ud83e\uddd1\ud83c\udffd\u200d\u2764\ufe0f\u200d\ud83e\uddd1\ud83c[\udffb\udffc\udffe\udfff]|\ud83e\uddd1\ud83c\udffd\u200d\ud83e\udd1d\u200d\ud83e\uddd1\ud83c[\udffb-\udfff]|\ud83e\uddd1\ud83c\udffe\u200d\u2764\ufe0f\u200d\ud83e\uddd1\ud83c[\udffb-\udffd\udfff]|\ud83e\uddd1\ud83c\udffe\u200d\ud83e\udd1d\u200d\ud83e\uddd1\ud83c[\udffb-\udfff]|\ud83e\uddd1\ud83c\udfff\u200d\u2764\ufe0f\u200d\ud83e\uddd1\ud83c[\udffb-\udffe]|\ud83e\uddd1\ud83c\udfff\u200d\ud83e\udd1d\u200d\ud83e\uddd1\ud83c[\udffb-\udfff]|\ud83d\udc68\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d\udc68|\ud83d\udc69\u200d\u2764\ufe0f\u200d\ud83d\udc8b\u200d\ud83d[\udc68\udc69]|\ud83d\udc68\u200d\u2764\ufe0f\u200d\ud83d\udc68|\ud83d\udc69\u200d\u2764\ufe0f\u200d\ud83d[\udc68\udc69]|\ud83e\uddd1\u200d\ud83e\udd1d\u200d\ud83e\uddd1|\ud83d\udc6b\ud83c[\udffb-\udfff]|\ud83d\udc6c\ud83c[\udffb-\udfff]|\ud83d\udc6d\ud83c[\udffb-\udfff]|\ud83d\udc8f\ud83c[\udffb-\udfff]|\ud83d\udc91\ud83c[\udffb-\udfff]|\ud83d[\udc6b-\udc6d\udc8f\udc91])|(?:\ud83d[\udc68\udc69]|\ud83e\uddd1)(?:\ud83c[\udffb-\udfff])?\u200d(?:\u2695\ufe0f|\u2696\ufe0f|\u2708\ufe0f|\ud83c[\udf3e\udf73\udf7c\udf84\udf93\udfa4\udfa8\udfeb\udfed]|\ud83d[\udcbb\udcbc\udd27\udd2c\ude80\ude92]|\ud83e[\uddaf-\uddb3\uddbc\uddbd])|(?:\ud83c[\udfcb\udfcc]|\ud83d[\udd74\udd75]|\u26f9)((?:\ud83c[\udffb-\udfff]|\ufe0f)\u200d[\u2640\u2642]\ufe0f)|(?:\ud83c[\udfc3\udfc4\udfca]|\ud83d[\udc6e\udc70\udc71\udc73\udc77\udc81\udc82\udc86\udc87\ude45-\ude47\ude4b\ude4d\ude4e\udea3\udeb4-\udeb6]|\ud83e[\udd26\udd35\udd37-\udd39\udd3d\udd3e\uddb8\uddb9\uddcd-\uddcf\uddd4\uddd6-\udddd])(?:\ud83c[\udffb-\udfff])?\u200d[\u2640\u2642]\ufe0f|(?:\ud83d\udc68\u200d\ud83d\udc68\u200d\ud83d\udc66\u200d\ud83d\udc66|\ud83d\udc68\u200d\ud83d\udc68\u200d\ud83d\udc67\u200d\ud83d[\udc66\udc67]|\ud83d\udc68\u200d\ud83d\udc69\u200d\ud83d\udc66\u200d\ud83d\udc66|\ud83d\udc68\u200d\ud83d\udc69\u200d\ud83d\udc67\u200d\ud83d[\udc66\udc67]|\ud83d\udc69\u200d\ud83d\udc69\u200d\ud83d\udc66\u200d\ud83d\udc66|\ud83d\udc69\u200d\ud83d\udc69\u200d\ud83d\udc67\u200d\ud83d[\udc66\udc67]|\ud83d\udc68\u200d\ud83d\udc66\u200d\ud83d\udc66|\ud83d\udc68\u200d\ud83d\udc67\u200d\ud83d[\udc66\udc67]|\ud83d\udc68\u200d\ud83d\udc68\u200d\ud83d[\udc66\udc67]|\ud83d\udc68\u200d\ud83d\udc69\u200d\ud83d[\udc66\udc67]|\ud83d\udc69\u200d\ud83d\udc66\u200d\ud83d\udc66|\ud83d\udc69\u200d\ud83d\udc67\u200d\ud83d[\udc66\udc67]|\ud83d\udc69\u200d\ud83d\udc69\u200d\ud83d[\udc66\udc67]|\ud83c\udff3\ufe0f\u200d\u26a7\ufe0f|\ud83c\udff3\ufe0f\u200d\ud83c\udf08|\ud83d\ude36\u200d\ud83c\udf2b\ufe0f|\u2764\ufe0f\u200d\ud83d\udd25|\u2764\ufe0f\u200d\ud83e\ude79|\ud83c\udff4\u200d\u2620\ufe0f|\ud83d\udc15\u200d\ud83e\uddba|\ud83d\udc3b\u200d\u2744\ufe0f|\ud83d\udc41\u200d\ud83d\udde8|\ud83d\udc68\u200d\ud83d[\udc66\udc67]|\ud83d\udc69\u200d\ud83d[\udc66\udc67]|\ud83d\udc6f\u200d\u2640\ufe0f|\ud83d\udc6f\u200d\u2642\ufe0f|\ud83d\ude2e\u200d\ud83d\udca8|\ud83d\ude35\u200d\ud83d\udcab|\ud83e\udd3c\u200d\u2640\ufe0f|\ud83e\udd3c\u200d\u2642\ufe0f|\ud83e\uddde\u200d\u2640\ufe0f|\ud83e\uddde\u200d\u2642\ufe0f|\ud83e\udddf\u200d\u2640\ufe0f|\ud83e\udddf\u200d\u2642\ufe0f|\ud83d\udc08\u200d\u2b1b)|[#*0-9]\ufe0f?\u20e3|(?:[©®\u2122\u265f]\ufe0f)|(?:\ud83c[\udc04\udd70\udd71\udd7e\udd7f\ude02\ude1a\ude2f\ude37\udf21\udf24-\udf2c\udf36\udf7d\udf96\udf97\udf99-\udf9b\udf9e\udf9f\udfcd\udfce\udfd4-\udfdf\udff3\udff5\udff7]|\ud83d[\udc3f\udc41\udcfd\udd49\udd4a\udd6f\udd70\udd73\udd76-\udd79\udd87\udd8a-\udd8d\udda5\udda8\uddb1\uddb2\uddbc\uddc2-\uddc4\uddd1-\uddd3\udddc-\uddde\udde1\udde3\udde8\uddef\uddf3\uddfa\udecb\udecd-\udecf\udee0-\udee5\udee9\udef0\udef3]|[\u203c\u2049\u2139\u2194-\u2199\u21a9\u21aa\u231a\u231b\u2328\u23cf\u23ed-\u23ef\u23f1\u23f2\u23f8-\u23fa\u24c2\u25aa\u25ab\u25b6\u25c0\u25fb-\u25fe\u2600-\u2604\u260e\u2611\u2614\u2615\u2618\u2620\u2622\u2623\u2626\u262a\u262e\u262f\u2638-\u263a\u2640\u2642\u2648-\u2653\u2660\u2663\u2665\u2666\u2668\u267b\u267f\u2692-\u2697\u2699\u269b\u269c\u26a0\u26a1\u26a7\u26aa\u26ab\u26b0\u26b1\u26bd\u26be\u26c4\u26c5\u26c8\u26cf\u26d1\u26d3\u26d4\u26e9\u26ea\u26f0-\u26f5\u26f8\u26fa\u26fd\u2702\u2708\u2709\u270f\u2712\u2714\u2716\u271d\u2721\u2733\u2734\u2744\u2747\u2757\u2763\u2764\u27a1\u2934\u2935\u2b05-\u2b07\u2b1b\u2b1c\u2b50\u2b55\u3030\u303d\u3297\u3299])(?:\ufe0f|(?!\ufe0e))|(?:(?:\ud83c[\udfcb\udfcc]|\ud83d[\udd74\udd75\udd90]|[\u261d\u26f7\u26f9\u270c\u270d])(?:\ufe0f|(?!\ufe0e))|(?:\ud83c[\udf85\udfc2-\udfc4\udfc7\udfca]|\ud83d[\udc42\udc43\udc46-\udc50\udc66-\udc69\udc6e\udc70-\udc78\udc7c\udc81-\udc83\udc85-\udc87\udcaa\udd7a\udd95\udd96\ude45-\ude47\ude4b-\ude4f\udea3\udeb4-\udeb6\udec0\udecc]|\ud83e[\udd0c\udd0f\udd18-\udd1c\udd1e\udd1f\udd26\udd30-\udd39\udd3d\udd3e\udd77\uddb5\uddb6\uddb8\uddb9\uddbb\uddcd-\uddcf\uddd1-\udddd]|[\u270a\u270b]))(?:\ud83c[\udffb-\udfff])?|(?:\ud83c\udff4\udb40\udc67\udb40\udc62\udb40\udc65\udb40\udc6e\udb40\udc67\udb40\udc7f|\ud83c\udff4\udb40\udc67\udb40\udc62\udb40\udc73\udb40\udc63\udb40\udc74\udb40\udc7f|\ud83c\udff4\udb40\udc67\udb40\udc62\udb40\udc77\udb40\udc6c\udb40\udc73\udb40\udc7f|\ud83c\udde6\ud83c[\udde8-\uddec\uddee\uddf1\uddf2\uddf4\uddf6-\uddfa\uddfc\uddfd\uddff]|\ud83c\udde7\ud83c[\udde6\udde7\udde9-\uddef\uddf1-\uddf4\uddf6-\uddf9\uddfb\uddfc\uddfe\uddff]|\ud83c\udde8\ud83c[\udde6\udde8\udde9\uddeb-\uddee\uddf0-\uddf5\uddf7\uddfa-\uddff]|\ud83c\udde9\ud83c[\uddea\uddec\uddef\uddf0\uddf2\uddf4\uddff]|\ud83c\uddea\ud83c[\udde6\udde8\uddea\uddec\udded\uddf7-\uddfa]|\ud83c\uddeb\ud83c[\uddee-\uddf0\uddf2\uddf4\uddf7]|\ud83c\uddec\ud83c[\udde6\udde7\udde9-\uddee\uddf1-\uddf3\uddf5-\uddfa\uddfc\uddfe]|\ud83c\udded\ud83c[\uddf0\uddf2\uddf3\uddf7\uddf9\uddfa]|\ud83c\uddee\ud83c[\udde8-\uddea\uddf1-\uddf4\uddf6-\uddf9]|\ud83c\uddef\ud83c[\uddea\uddf2\uddf4\uddf5]|\ud83c\uddf0\ud83c[\uddea\uddec-\uddee\uddf2\uddf3\uddf5\uddf7\uddfc\uddfe\uddff]|\ud83c\uddf1\ud83c[\udde6-\udde8\uddee\uddf0\uddf7-\uddfb\uddfe]|\ud83c\uddf2\ud83c[\udde6\udde8-\udded\uddf0-\uddff]|\ud83c\uddf3\ud83c[\udde6\udde8\uddea-\uddec\uddee\uddf1\uddf4\uddf5\uddf7\uddfa\uddff]|\ud83c\uddf4\ud83c\uddf2|\ud83c\uddf5\ud83c[\udde6\uddea-\udded\uddf0-\uddf3\uddf7-\uddf9\uddfc\uddfe]|\ud83c\uddf6\ud83c\udde6|\ud83c\uddf7\ud83c[\uddea\uddf4\uddf8\uddfa\uddfc]|\ud83c\uddf8\ud83c[\udde6-\uddea\uddec-\uddf4\uddf7-\uddf9\uddfb\uddfd-\uddff]|\ud83c\uddf9\ud83c[\udde6\udde8\udde9\uddeb-\udded\uddef-\uddf4\uddf7\uddf9\uddfb\uddfc\uddff]|\ud83c\uddfa\ud83c[\udde6\uddec\uddf2\uddf3\uddf8\uddfe\uddff]|\ud83c\uddfb\ud83c[\udde6\udde8\uddea\uddec\uddee\uddf3\uddfa]|\ud83c\uddfc\ud83c[\uddeb\uddf8]|\ud83c\uddfd\ud83c\uddf0|\ud83c\uddfe\ud83c[\uddea\uddf9]|\ud83c\uddff\ud83c[\udde6\uddf2\uddfc]|\ud83c[\udccf\udd8e\udd91-\udd9a\udde6-\uddff\ude01\ude32-\ude36\ude38-\ude3a\ude50\ude51\udf00-\udf20\udf2d-\udf35\udf37-\udf7c\udf7e-\udf84\udf86-\udf93\udfa0-\udfc1\udfc5\udfc6\udfc8\udfc9\udfcf-\udfd3\udfe0-\udff0\udff4\udff8-\udfff]|\ud83d[\udc00-\udc3e\udc40\udc44\udc45\udc51-\udc65\udc6a\udc6f\udc79-\udc7b\udc7d-\udc80\udc84\udc88-\udc8e\udc90\udc92-\udca9\udcab-\udcfc\udcff-\udd3d\udd4b-\udd4e\udd50-\udd67\udda4\uddfb-\ude44\ude48-\ude4a\ude80-\udea2\udea4-\udeb3\udeb7-\udebf\udec1-\udec5\uded0-\uded2\uded5-\uded7\udeeb\udeec\udef4-\udefc\udfe0-\udfeb]|\ud83e[\udd0d\udd0e\udd10-\udd17\udd1d\udd20-\udd25\udd27-\udd2f\udd3a\udd3c\udd3f-\udd45\udd47-\udd76\udd78\udd7a-\uddb4\uddb7\uddba\uddbc-\uddcb\uddd0\uddde-\uddff\ude70-\ude74\ude78-\ude7a\ude80-\ude86\ude90-\udea8\udeb0-\udeb6\udec0-\udec2\uded0-\uded6]|[\u23e9-\u23ec\u23f0\u23f3\u267e\u26ce\u2705\u2728\u274c\u274e\u2753-\u2755\u2795-\u2797\u27b0\u27bf\ue50a])|\ufe0f/g;
